import React from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { Users, Stethoscope, BedDouble, ChevronRight } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";

const PRESENZA = { presente: ["Presente", "bg-emerald-100 text-emerald-800"], ospedale: ["Ricoverato", "bg-red-100 text-red-800"], uscita: ["Uscito", "bg-amber-100 text-amber-800"] };

export const useMieiOspiti = (opId) => useQuery({
  queryKey: ["miei-ospiti", opId], enabled: !!opId, retry: false,
  queryFn: async () => (await api.get("/firma/mio-account/ospiti", { params: { op_id: opId } })).data,
});

export default function MieiOspiti({ opId }) {
  const { structures = [], can } = useAuth();
  const { data: ospiti = [], isLoading } = useMieiOspiti(opId);
  const sNome = (id) => structures.find((s) => s.id === id)?.nome || "";
  const linkable = typeof can === "function" && can("ospiti");
  if (isLoading) return <p className="text-sm text-muted-foreground">Caricamento…</p>;
  return (
    <div data-testid="miei-ospiti">
      <div className="flex items-center gap-2 mb-1"><Users size={18} className="text-violet-600" /><h3 className="font-head font-bold">I miei ospiti</h3><span className="text-xs font-bold rounded-full px-2 py-0.5 bg-violet-100 text-violet-800">{ospiti.length}</span></div>
      <p className="text-xs text-muted-foreground mb-4">Gli ospiti di cui sei l'operatore responsabile.</p>
      {ospiti.length === 0 ? <p className="text-sm text-muted-foreground" data-testid="miei-ospiti-empty">Non risulti ancora responsabile di nessun ospite.</p> : (
        <div className="grid sm:grid-cols-2 gap-3">
          {ospiti.map((o) => {
            const [pl, pc] = PRESENZA[o.presenza] || ["", ""];
            const body = (
              <div className="rounded-xl border bg-white p-3.5 flex items-start gap-3 hover:shadow-sm transition-shadow" data-testid={`mio-ospite-${o.id}`}>
                <div className="h-10 w-10 rounded-full bg-violet-100 text-violet-800 font-bold grid place-items-center shrink-0">{(o.nome[0] || "") + (o.cognome[0] || "")}</div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold truncate">{o.cognome} {o.nome}</div>
                  <div className="text-[11px] text-muted-foreground flex items-center gap-1 flex-wrap"><BedDouble size={11} /> Camera {o.camera || "—"}{o.posto_letto ? ` · posto ${o.posto_letto}` : ""}{sNome(o.structure_id) ? ` · ${sNome(o.structure_id)}` : ""}</div>
                  <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
                    {pl && <span className={`text-[10px] font-bold rounded-full px-1.5 py-0.5 ${pc}`}>{pl}</span>}
                    {o.medico_base && <span className="text-[10px] font-semibold rounded-full px-1.5 py-0.5 bg-sky-50 text-sky-800 inline-flex items-center gap-1"><Stethoscope size={10} /> {o.medico_base}</span>}
                  </div>
                </div>
                {linkable && <ChevronRight size={16} className="text-muted-foreground mt-2" />}
              </div>
            );
            return linkable ? <Link key={o.id} to={`/ospiti/${o.id}`}>{body}</Link> : <div key={o.id}>{body}</div>;
          })}
        </div>
      )}
    </div>
  );
}
